import { useEffect } from 'react'
import { readCirclesAutoOpen, clearCirclesAutoOpen } from '../shared/onboardingAutoOpen'

/**
 * Consumer half of the onboarding → drawer hand-off: after the user joins a
 * circle on the final onboarding screen (`writeCirclesAutoOpen(uid)`), the
 * next mount of the gated nav entry fires `onAutoOpen` exactly once so the
 * drawer opens to the joined circle. Reading + clearing happen synchronously
 * INSIDE the effect, so a StrictMode double-invoke can't fire twice; a
 * disabled gate leaves the signal in place for a later eligible mount. A
 * signal written by a *different* uid (shared browser, account switch) is
 * discarded — cleared without opening.
 *
 * `onAutoOpen` must be referentially stable (`useCallback`) — it's in the deps
 * so the effect re-arms rather than closing over a stale callback.
 *
 * @param enabled    The nav-entry gate (config on AND the viewer has a circle —
 *                   `showEntry` in `CirclesNavEntry`).
 * @param uid        The current viewer's uid (`selectAuthUid`).
 * @param onAutoOpen Opens the drawer (and tracks the `auto` trigger).
 */
export default function useCirclesAutoOpen (
  enabled: boolean,
  uid: string | undefined,
  onAutoOpen: () => void,
): void {
  useEffect(() => {
    if (!enabled) return
    // Auth not yet hydrated — leave the signal untouched so a later mount (once
    // uid resolves) can still consume it. Reading/clearing before uid is known
    // would burn the hand-off and the drawer would never auto-open.
    if (!uid) return
    const signal = readCirclesAutoOpen()
    if (!signal) return
    // Consume it now uid is known: either we open, or it's a stale/foreign
    // signal (shared browser, account switch) that we discard.
    clearCirclesAutoOpen()
    if (signal !== uid) return
    onAutoOpen()
  }, [enabled, uid, onAutoOpen])
}
